import { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './RazorpayReconcile.css';

const API_URL = process.env.REACT_APP_API_URL;

const fmt = v => v != null ? `₹${parseFloat(v).toFixed(2)}` : '—';
const fmtDate = iso => iso ? iso.slice(0, 10) : '—';
const fmtTime = iso => iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false }) : '—';

export default function RazorpayReconcile() {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const today = new Date();
  const isoToday = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  const [activeTab, setActiveTab] = useState('razorpay');

  const [loadDate, setLoadDate] = useState({ from: isoToday, to: isoToday });
  const [loadBusy, setLoadBusy] = useState(false);
  const [loadResult, setLoadResult] = useState(null);
  const [loadError, setLoadError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalReceiptName, setModalReceiptName] = useState('');
  const [modalReceiptId, setModalReceiptId] = useState('');
  const [modalReceiptAmount, setModalReceiptAmount] = useState(null);
  const [preview, setPreview] = useState([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  const [walletOrders, setWalletOrders] = useState(null);
  const [walletBusy, setWalletBusy] = useState(false);
  const [walletReconciling, setWalletReconciling] = useState(false);
  const [walletResult, setWalletResult] = useState(null);
  const [walletError, setWalletError] = useState('');

  // Payment Issues tab
  const [piDate, setPiDate] = useState({ from: isoToday, to: isoToday });
  const [piBusy, setPiBusy] = useState(false);
  const [piResult, setPiResult] = useState(null);
  const [piError, setPiError] = useState('');
  const [piFixing, setPiFixing] = useState({});
  const [piFixResult, setPiFixResult] = useState({});

  const fetchWalletOrders = async () => {
    setWalletBusy(true);
    setWalletError('');
    setWalletResult(null);
    try {
      const res = await axios.get(`${API_URL}/order-receipt-details/wallet-preview`, auth());
      setWalletOrders(res.data);
    } catch (err) {
      setWalletError(err.response?.data?.error || err.message);
      setWalletOrders([]);
    } finally {
      setWalletBusy(false);
    }
  };

  const handleWalletTab = () => {
    setActiveTab('wallet');
    if (walletOrders === null) fetchWalletOrders();
  };

  const handleWalletReconcile = async () => {
    setWalletReconciling(true);
    setWalletResult(null);
    try {
      const res = await axios.post(`${API_URL}/order-receipt-details/wallet-auto-reconcile`, {}, auth());
      setWalletResult(res.data);
      fetchWalletOrders();
    } catch (err) {
      setWalletResult({ error: err.response?.data?.error || err.message });
    } finally {
      setWalletReconciling(false);
    }
  };

  const walletPending = (walletOrders || []).filter(o => !o.already_reconciled).length;

  const handleLoad = async () => {
    setLoadBusy(true);
    setLoadError('');
    setLoadResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/pay/settlement-recon/load`,
        { from_date: loadDate.from, to_date: loadDate.to },
        auth()
      );
      setLoadResult(res.data);
    } catch (err) {
      setLoadError(err.response?.data?.error || err.message);
    } finally {
      setLoadBusy(false);
    }
  };

  const openModal = async (receiptId, receiptName, receiptAmount) => {
    setModalReceiptId(receiptId);
    setModalReceiptName(receiptName);
    setModalReceiptAmount(receiptAmount);
    setPreview([]);
    setReconcileResult(null);
    setModalOpen(true);
    setPreviewBusy(true);
    try {
      const res = await axios.get(
        `${API_URL}/order-receipt-details/razorpay-preview?receipt_id=${receiptId}`,
        auth()
      );
      setPreview(res.data);
    } catch {
      setPreview([]);
    } finally {
      setPreviewBusy(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalReceiptId('');
    setModalReceiptName('');
    setModalReceiptAmount(null);
    setPreview([]);
    setReconcileResult(null);
  };

  const handleReconcile = async () => {
    if (!modalReceiptId) return;
    setReconciling(true);
    setReconcileResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/order-receipt-details/razorpay-auto-reconcile`,
        { receipt_id: modalReceiptId },
        auth()
      );
      setReconcileResult(res.data);
      const prev = await axios.get(
        `${API_URL}/order-receipt-details/razorpay-preview?receipt_id=${modalReceiptId}`,
        auth()
      );
      setPreview(prev.data);
    } catch (err) {
      setReconcileResult({ error: err.response?.data?.error || err.message });
    } finally {
      setReconciling(false);
    }
  };

  const pendingCount = preview.filter(r => !r.processed && !r.already_linked).length;

  // Payment Issues handlers
  const handlePiFetch = async () => {
    setPiBusy(true);
    setPiError('');
    setPiResult(null);
    setPiFixing({});
    setPiFixResult({});
    try {
      const res = await axios.get(
        `${API_URL}/pay/reconcile?from=${piDate.from}&to=${piDate.to}`,
        auth()
      );
      setPiResult(res.data);
    } catch (err) {
      setPiError(err.response?.data?.error || err.message);
    } finally {
      setPiBusy(false);
    }
  };

  const handlePiFix = async (razorpay_order_id, razorpay_payment_id, case_type, notes_order_id = null) => {
    const key = razorpay_payment_id;
    setPiFixing(f => ({ ...f, [key]: true }));
    setPiFixResult(r => ({ ...r, [key]: null }));
    try {
      const body = { razorpay_order_id, razorpay_payment_id, case_type };
      if (notes_order_id) body.notes_order_id = notes_order_id;
      const res = await axios.post(
        `${API_URL}/pay/reconcile/fix`,
        body,
        auth()
      );
      setPiFixResult(r => ({ ...r, [key]: { ok: true, data: res.data } }));
      // refresh the list
      const refresh = await axios.get(
        `${API_URL}/pay/reconcile?from=${piDate.from}&to=${piDate.to}`,
        auth()
      );
      setPiResult(refresh.data);
    } catch (err) {
      setPiFixResult(r => ({ ...r, [key]: { ok: false, error: err.response?.data?.error || err.message } }));
    } finally {
      setPiFixing(f => ({ ...f, [key]: false }));
    }
  };

  // Refund rows are deductions — gross shown as negative, fee as positive charge.
  // Adjustment rows use net_amount sign to determine direction.
  // Gross − Fee = Net holds for every row type uniformly.
  const rowGross = (r) => {
    if (r.txn_kind === 'refund') return -parseFloat(r.ref_total || 0);
    if (r.txn_kind === 'adjustment') {
      // If net is negative it's a settlement deduction; show gross as negative
      return parseFloat(r.net_amount || 0) < 0
        ? -parseFloat(r.ref_total || 0)
        : parseFloat(r.ref_total || 0);
    }
    return parseFloat(r.ref_total || 0);
  };
  const rowFee  = (r) => parseFloat(r.fee || 0); // always a positive charge
  const rowNet  = (r) => rowGross(r) - rowFee(r);

  const totRefTotal = preview.reduce((s, r) => s + rowGross(r), 0);
  const totFee      = preview.reduce((s, r) => s + rowFee(r),   0);
  const totNet      = totRefTotal - totFee;

  const piPaymentCols = (
    <tr>
      <th>Payment ID</th>
      <th>Razorpay Order ID</th>
      <th>Contact</th>
      <th className="rr-num">Amount</th>
      <th>Paid At (IST)</th>
    </tr>
  );

  const piPaymentCells = (p) => (
    <>
      <td><code style={{ fontSize: 11 }}>{p.razorpay_payment_id}</code></td>
      <td><code style={{ fontSize: 11 }}>{p.razorpay_order_id || '—'}</code></td>
      <td>{p.contact || p.email || '—'}</td>
      <td className="rr-num"><strong>{fmt(p.amount)}</strong></td>
      <td>{fmtTime(p.created_at)}</td>
    </>
  );

  return (
    <div className="rr-page">
      <h1 className="rr-title">Reconcile</h1>

      {/* Tabs */}
      <div className="rr-tabs">
        <button
          className={`rr-tab ${activeTab === 'razorpay' ? 'rr-tab-active' : ''}`}
          onClick={() => setActiveTab('razorpay')}
        >
          Bank / Razorpay
        </button>
        <button
          className={`rr-tab ${activeTab === 'wallet' ? 'rr-tab-active' : ''}`}
          onClick={handleWalletTab}
        >
          Wallet Payments
        </button>
        <button
          className={`rr-tab ${activeTab === 'issues' ? 'rr-tab-active' : ''}`}
          onClick={() => setActiveTab('issues')}
        >
          Payment Issues
        </button>
      </div>

      {/* ── Wallet tab ── */}
      {activeTab === 'wallet' && (
        <div className="rr-section">
          <h2 className="rr-section-title">Wallet Payment Reconciliation</h2>
          <p className="rr-hint">
            Orders paid (fully or partially) with wallet credit. Auto-reconcile creates receipt lines
            (type 31) linking each order to its wallet payment.
          </p>

          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <button className="rr-btn-load" onClick={fetchWalletOrders} disabled={walletBusy}>
              {walletBusy ? 'Loading...' : 'Refresh'}
            </button>
            {walletPending > 0 && (
              <button className="rr-btn-run" onClick={handleWalletReconcile} disabled={walletReconciling}>
                {walletReconciling ? 'Reconciling...' : `Auto-Reconcile (${walletPending} pending)`}
              </button>
            )}
          </div>

          {walletError && <p className="rr-error">{walletError}</p>}

          {walletResult && !walletResult.error && (
            <div className="rr-result-ok" style={{ marginBottom: 12 }}>
              Processed <strong>{walletResult.processed}</strong> order(s).
            </div>
          )}
          {walletResult?.error && (
            <div className="rr-error" style={{ marginBottom: 12 }}>{walletResult.error}</div>
          )}

          {walletBusy && <p className="rr-hint">Loading...</p>}

          {!walletBusy && walletOrders !== null && walletOrders.length === 0 && (
            <p className="rr-empty">No wallet-paid orders found.</p>
          )}

          {!walletBusy && walletOrders && walletOrders.length > 0 && (
            <table className="rr-table">
              <thead>
                <tr>
                  <th>Order No</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th className="rr-num">Wallet Cash</th>
                  <th className="rr-num">Wallet Points</th>
                  <th className="rr-num">Total Wallet</th>
                  <th className="rr-num">Razorpay</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {walletOrders.map(o => {
                  const isCancelled = o.order_state === 'Cancelled';
                  const rowClass = o.already_reconciled ? 'rr-done' : isCancelled ? 'rr-refund' : '';
                  const displayAmount = isCancelled ? -parseFloat(o.wallet_total) : parseFloat(o.wallet_total);
                  return (
                  <tr key={o.order_id} className={rowClass}>
                    <td>{o.order_no}</td>
                    <td>{o.customer_name}</td>
                    <td>{o.order_date ? o.order_date.slice(0, 10) : '—'}</td>
                    <td className="rr-num">{fmt(o.wallet_cash_amount)}</td>
                    <td className="rr-num">{fmt(o.wallet_points_amount)}</td>
                    <td className="rr-num"><strong>{fmt(displayAmount)}</strong></td>
                    <td className="rr-num">{parseFloat(o.razorpay_amount) > 0 ? fmt(o.razorpay_amount) : '—'}</td>
                    <td>
                      {isCancelled
                        ? <span className="rr-tag rr-tag-refund">Refund</span>
                        : parseFloat(o.razorpay_amount) > 0
                          ? <span className="rr-tag rr-tag-order">Split</span>
                          : <span className="rr-tag rr-tag-wallet">Wallet Only</span>}
                      {o.plan_your_week && (
                        <span className="rr-tag rr-tag-pyw" style={{ marginLeft: 4 }}>PYW</span>
                      )}
                    </td>
                    <td>
                      {o.already_reconciled
                        ? <span className="rr-tag rr-tag-done">Done</span>
                        : <span className="rr-tag rr-tag-pending">Pending</span>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Razorpay tab ── */}
      {activeTab === 'razorpay' && <div className="rr-section">
        <h2 className="rr-section-title">Load Settlement Data</h2>
        <p className="rr-hint">
          Fetch settlement transactions from Razorpay for a date range and match them to
          uploaded bank receipts (via UTR / Cheque No).
        </p>
        <div className="rr-row">
          <label className="rr-label">
            From
            <input className="rr-input" type="date"
              value={loadDate.from}
              onChange={e => setLoadDate(d => ({ ...d, from: e.target.value }))} />
          </label>
          <label className="rr-label">
            To
            <input className="rr-input" type="date"
              value={loadDate.to}
              onChange={e => setLoadDate(d => ({ ...d, to: e.target.value }))} />
          </label>
          <button className="rr-btn-load" onClick={handleLoad} disabled={loadBusy}>
            {loadBusy ? 'Loading...' : 'Load from Razorpay'}
          </button>
        </div>

        {loadError && <p className="rr-error">{loadError}</p>}

        {loadResult && (
          <>
            <div className="rr-load-result">
              Fetched <strong>{loadResult.fetched}</strong> items &nbsp;·&nbsp;
              Upserted <strong>{loadResult.upserted}</strong> payments &nbsp;·&nbsp;
              Matched <strong>{loadResult.receipts?.length ?? 0}</strong> bank receipt(s)
            </div>

            {loadResult.receipts?.length > 0 && (
              <div className="rr-matched-wrap">
                <h3 className="rr-sub-title">Matched Receipts</h3>
                <table className="rr-table">
                  <thead>
                    <tr>
                      <th>Receipt Name</th>
                      <th className="rr-num">Amount</th>
                      <th>Reconciled</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadResult.receipts.map(r => (
                      <tr key={r.id}>
                        <td>{r.name}</td>
                        <td className="rr-num">{fmt(r.receipt_amount)}</td>
                        <td>
                          <span className={`rr-tag ${r.reconciled === 'Y' ? 'rr-tag-done' : 'rr-tag-pending'}`}>
                            {r.reconciled === 'Y' ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <button className="rr-btn-reconcile" onClick={() => openModal(r.id, r.name, r.receipt_amount)}>
                            Reconcile
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {loadResult.receipts?.length === 0 && (
              <p className="rr-empty" style={{ marginTop: 12 }}>
                No bank receipts matched via UTR for this date range.
              </p>
            )}
          </>
        )}
      </div>}

      {/* ── Payment Issues tab ── */}
      {activeTab === 'issues' && (
        <div className="rr-section">
          <h2 className="rr-section-title">Payment Issues</h2>
          <p className="rr-hint">
            Find captured Razorpay payments that are not yet recorded in LH. Identify orders not created
            (Case 1) or orders stuck in Payment Requested (Case 2) and fix them individually.
          </p>

          <div className="rr-row">
            <label className="rr-label">
              From
              <input className="rr-input" type="date"
                value={piDate.from}
                onChange={e => setPiDate(d => ({ ...d, from: e.target.value }))} />
            </label>
            <label className="rr-label">
              To
              <input className="rr-input" type="date"
                value={piDate.to}
                onChange={e => setPiDate(d => ({ ...d, to: e.target.value }))} />
            </label>
            <button className="rr-btn-load" onClick={handlePiFetch} disabled={piBusy}>
              {piBusy ? 'Fetching...' : 'Fetch from Razorpay'}
            </button>
          </div>

          {piError && <p className="rr-error">{piError}</p>}

          {piResult && (
            <>
              <div className="rr-load-result">
                <span className="rr-pi-stat rr-pi-ok">{piResult.matched?.length ?? 0} matched</span>
                <span className="rr-pi-stat rr-pi-warn">{piResult.case2?.length ?? 0} unplaced</span>
                <span className="rr-pi-stat rr-pi-err">{piResult.case1?.length ?? 0} order not created</span>
                {piResult.case_weekly?.length > 0 && (
                  <span className="rr-pi-stat rr-pi-warn">{piResult.case_weekly.length} weekly pending</span>
                )}
                {piResult.unresolved?.length > 0 && (
                  <span className="rr-pi-stat rr-pi-gray">{piResult.unresolved.length} unresolved</span>
                )}
              </div>

              {/* Case 2 — Payment captured but order stuck in Payment Requested */}
              {piResult.case2?.length > 0 && (
                <div className="rr-matched-wrap">
                  <h3 className="rr-sub-title rr-sub-warn">
                    Unplaced Orders — Payment Requested ({piResult.case2.length})
                  </h3>
                  <p className="rr-hint">
                    Payment was captured but the order state was not moved to Placed. Fix creates the
                    lh_online_payments record and transitions the order to Placed.
                  </p>
                  <table className="rr-table">
                    <thead>{piPaymentCols}</thead>
                    <tbody>
                      {piResult.case2.map(p => {
                        const key = p.razorpay_payment_id;
                        const fr = piFixResult[key];
                        return (
                          <tr key={key}>
                            {piPaymentCells(p)}
                            <td>
                              {p.order_no
                                ? <span style={{ fontSize: 12 }}>{p.order_no} · {p.order_lc_state}</span>
                                : <span className="rr-hint">—</span>}
                              {p.notes_fallback && (
                                <span className="rr-tag rr-tag-adjustment" style={{ marginLeft: 4, fontSize: 10 }}>Notes</span>
                              )}
                            </td>
                            <td>
                              {fr?.ok
                                ? <span className="rr-tag rr-tag-done">Fixed</span>
                                : (
                                  <>
                                    <button
                                      className="rr-btn-reconcile"
                                      disabled={piFixing[key]}
                                      onClick={() => handlePiFix(p.razorpay_order_id, p.razorpay_payment_id, 'case2', p.notes_fallback ? p.order_id : null)}
                                    >
                                      {piFixing[key] ? 'Fixing...' : 'Fix'}
                                    </button>
                                    {fr?.error && <span className="rr-error" style={{ marginLeft: 6, fontSize: 12 }}>{fr.error}</span>}
                                  </>
                                )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Case Weekly — weekly payment captured but not yet processed */}
              {piResult.case_weekly?.length > 0 && (
                <div className="rr-matched-wrap">
                  <h3 className="rr-sub-title rr-sub-warn">
                    Weekly Payment Not Processed ({piResult.case_weekly.length})
                  </h3>
                  <p className="rr-hint">
                    Payment was captured for a Plan Your Week order but the weekly payment record
                    was not processed. Fix creates the online payment record and updates all orders
                    in the weekly plan.
                  </p>
                  <table className="rr-table">
                    <thead>
                      <tr>
                        <th>Payment ID</th>
                        <th>Razorpay Order ID</th>
                        <th>Contact</th>
                        <th className="rr-num">Amount</th>
                        <th>Paid At (IST)</th>
                        <th>User ID</th>
                        <th>Week</th>
                        <th>Orders</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {piResult.case_weekly.map(p => {
                        const key = p.razorpay_payment_id;
                        const fr = piFixResult[key];
                        return (
                          <tr key={key}>
                            <td><code style={{ fontSize: 11 }}>{p.razorpay_payment_id}</code></td>
                            <td><code style={{ fontSize: 11 }}>{p.razorpay_order_id || '—'}</code></td>
                            <td>{p.contact || p.email || '—'}</td>
                            <td className="rr-num"><strong>{fmt(p.amount)}</strong></td>
                            <td>{fmtTime(p.created_at)}</td>
                            <td>{p.user_id}</td>
                            <td style={{ fontSize: 12 }}>{fmtDate(p.week_start)} – {fmtDate(p.week_end)}</td>
                            <td style={{ textAlign: 'center' }}>{p.order_count}</td>
                            <td>
                              {fr?.ok
                                ? <span className="rr-tag rr-tag-done">Fixed</span>
                                : (
                                  <>
                                    <button
                                      className="rr-btn-reconcile"
                                      disabled={piFixing[key]}
                                      onClick={() => handlePiFix(p.razorpay_order_id, p.razorpay_payment_id, 'case_weekly')}
                                    >
                                      {piFixing[key] ? 'Fixing...' : 'Fix'}
                                    </button>
                                    {fr?.error && <span className="rr-error" style={{ marginLeft: 6, fontSize: 12 }}>{fr.error}</span>}
                                  </>
                                )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Case 1 — Payment captured but order was never created */}
              {piResult.case1?.length > 0 && (
                <div className="rr-matched-wrap">
                  <h3 className="rr-sub-title rr-sub-err">
                    Order Not Created ({piResult.case1.length})
                  </h3>
                  <p className="rr-hint">
                    Payment was captured but no order was created in LH (pending order exists in
                    lh_pending_orders). Fix replays order creation from the saved cart.
                  </p>
                  <table className="rr-table">
                    <thead>
                      <tr>
                        <th>Payment ID</th>
                        <th>Razorpay Order ID</th>
                        <th>Customer</th>
                        <th className="rr-num">Amount</th>
                        <th>Paid At (IST)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {piResult.case1.map(p => {
                        const key = p.razorpay_payment_id;
                        const fr = piFixResult[key];
                        return (
                          <tr key={key}>
                            <td><code style={{ fontSize: 11 }}>{p.razorpay_payment_id}</code></td>
                            <td><code style={{ fontSize: 11 }}>{p.razorpay_order_id || '—'}</code></td>
                            <td>{p.customer_name || p.contact || p.email || '—'}</td>
                            <td className="rr-num"><strong>{fmt(p.amount)}</strong></td>
                            <td>{fmtTime(p.created_at)}</td>
                            <td>
                              {fr?.ok
                                ? (
                                  <span className="rr-tag rr-tag-done">
                                    Fixed — {fr.data?.orders?.map(o => o.order_no).join(', ')}
                                  </span>
                                )
                                : (
                                  <>
                                    <button
                                      className="rr-btn-reconcile"
                                      disabled={piFixing[key]}
                                      onClick={() => handlePiFix(p.razorpay_order_id, p.razorpay_payment_id, 'case1')}
                                    >
                                      {piFixing[key] ? 'Fixing...' : 'Fix'}
                                    </button>
                                    {fr?.error && <span className="rr-error" style={{ marginLeft: 6, fontSize: 12 }}>{fr.error}</span>}
                                  </>
                                )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Unresolved — captured but no pending record found */}
              {piResult.unresolved?.length > 0 && (
                <div className="rr-matched-wrap">
                  <h3 className="rr-sub-title rr-sub-gray">Unresolved ({piResult.unresolved.length})</h3>
                  <p className="rr-hint">
                    Captured but not found in lh_online_payments or any pending table. May be wallet
                    loads, test payments, or payments from a different flow.
                  </p>
                  <table className="rr-table">
                    <thead>
                      <tr>
                        <th>Payment ID</th>
                        <th>Razorpay Order ID</th>
                        <th>Contact</th>
                        <th className="rr-num">Amount</th>
                        <th>Paid At (IST)</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {piResult.unresolved.map(p => (
                        <tr key={p.razorpay_payment_id}>
                          <td><code style={{ fontSize: 11 }}>{p.razorpay_payment_id}</code></td>
                          <td><code style={{ fontSize: 11 }}>{p.razorpay_order_id || '—'}</code></td>
                          <td>{p.contact || p.email || '—'}</td>
                          <td className="rr-num"><strong>{fmt(p.amount)}</strong></td>
                          <td>{fmtTime(p.created_at)}</td>
                          <td style={{ fontSize: 11, color: '#666' }}>
                            {p.notes?.purpose || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {piResult.case1?.length === 0 && piResult.case2?.length === 0 &&
               piResult.case_weekly?.length === 0 && piResult.unresolved?.length === 0 && (
                <p className="rr-empty">No issues for the selected period.</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Reconcile modal */}
      {modalOpen && (
        <div className="rr-modal-overlay" onClick={closeModal}>
          <div className="rr-modal" onClick={e => e.stopPropagation()}>
            <div className="rr-modal-hdr">
              <span className="rr-modal-title">{modalReceiptName}</span>
              <button className="rr-modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="rr-modal-body">
              {/* Receipt amount banner */}
              <div className="rr-receipt-amount">
                Receipt Amount: <strong>{fmt(modalReceiptAmount)}</strong>
              </div>

              {previewBusy && (
                <p className="rr-hint" style={{ marginTop: 12 }}>Checking for Razorpay settlements...</p>
              )}

              {!previewBusy && preview.length === 0 && (
                <p className="rr-empty">
                  No Razorpay settlement transactions are linked to this receipt.
                  Load data first, or confirm the UTR in the bank statement matches.
                </p>
              )}

              {preview.length > 0 && (
                <>
                  <table className="rr-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Reference</th>
                        <th className="rr-num">Gross</th>
                        <th className="rr-num">Fee (incl. Tax)</th>
                        <th className="rr-num">Net (Receipt Line)</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map(row => {
                        return (
                          <tr key={row.txn_id} className={
                            row.txn_kind === 'refund' ? 'rr-refund'
                            : row.txn_kind === 'adjustment' ? 'rr-adjustment'
                            : row.processed ? 'rr-done'
                            : row.already_linked ? 'rr-warn' : ''
                          }>
                            <td>
                              {row.txn_kind === 'wallet_load'
                                ? <span className="rr-tag rr-tag-wallet">Wallet Load</span>
                                : row.txn_kind === 'refund'
                                  ? <span className="rr-tag rr-tag-refund">Refund</span>
                                  : row.txn_kind === 'adjustment'
                                    ? <span className="rr-tag rr-tag-adjustment">Adjustment</span>
                                    : <span className="rr-tag rr-tag-order">Order</span>}
                              {row.plan_your_week && (
                                <span className="rr-tag rr-tag-pyw" style={{ marginLeft: 4 }}>
                                  {row.sales_channel_id === 22 ? 'PPO' : 'PYW'}
                                </span>
                              )}
                            </td>
                            <td>
                              {row.ref_label}
                              {row.plan_your_week && row.batch_size > 1 && (
                                <span className="rr-batch-hint"> · batch of {row.batch_size}</span>
                              )}
                            </td>
                            <td className="rr-num">{fmt(rowGross(row))}</td>
                            <td className="rr-num">{fmt(rowFee(row))}</td>
                            <td className="rr-num"><strong>{fmt(rowNet(row))}</strong></td>
                            <td>
                              {row.processed
                                ? <span className="rr-tag rr-tag-done">Done</span>
                                : row.already_linked
                                  ? <span className="rr-tag rr-tag-warn">Already linked</span>
                                  : <span className="rr-tag rr-tag-pending">Pending</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="rr-tfoot">
                        <td colSpan={2}><strong>Total</strong></td>
                        <td className="rr-num"><strong>{fmt(totRefTotal)}</strong></td>
                        <td className="rr-num"><strong>{fmt(totFee)}</strong></td>
                        <td className="rr-num"><strong>{fmt(totNet)}</strong></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* Manual comparison line */}
                  {(() => {
                    const calcNet = parseFloat((totRefTotal - totFee).toFixed(2));
                    const recAmt  = parseFloat(parseFloat(modalReceiptAmount || 0).toFixed(2));
                    const match   = calcNet === recAmt;
                    const cls     = match ? 'rr-comparison rr-comparison-ok' : 'rr-comparison rr-comparison-diff';
                    return (
                      <div className={cls}>
                        Gross {fmt(totRefTotal)} − Fee {fmt(totFee)}
                        {' = '}<strong>{fmt(calcNet)}</strong>
                        <span className="rr-comparison-sep">vs</span>
                        Receipt <strong>{fmt(recAmt)}</strong>
                      </div>
                    );
                  })()}

                  {pendingCount > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <button className="rr-btn-run" onClick={handleReconcile} disabled={reconciling}>
                        {reconciling
                          ? 'Reconciling...'
                          : `Run Auto-Reconcile (${pendingCount} pending)`}
                      </button>
                    </div>
                  )}

                  {reconcileResult && !reconcileResult.error && (
                    <div className="rr-result-ok">
                      Processed <strong>{reconcileResult.processed}</strong> transaction(s). Receipt lines and
                      Payment Gateway Charges expenses have been created.
                    </div>
                  )}
                  {reconcileResult?.error && (
                    <div className="rr-error">{reconcileResult.error}</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
